import { _ } from "../../../core/polyglot/polyglot.js";
import { classicReportIcon } from "../../../core/elements/icons/index.js";
import { formatAsDashedYearMonthDay } from "../../../core/helpers/date-formatting-util.js";
import { iconAndText } from "../../../core/elements/index.js";
import { none } from "../../../core/elements/size/index.js";
import { router } from "@foretagsplatsen/widgetjs";
import AbstractPeriodReportSectionVisitor from "../../../models/accounting/periodReport/section/AbstractPeriodReportSectionVisitor.js";
import AgedAccountsSummaryWidget from "./AgedAccountsSummaryWidget/AgedAccountsSummaryWidget.js";
import AlertMessageWidget from "../../../core/widgets/AlertMessageWidget/AlertMessageWidget.js";
import BusinessOverviewReportWidget from "./BusinessOverviewReportWidget.js";
import condensedStatementWidget from "./condensedStatementWidget/condensedStatementWidget.js";
import keyFigureOverviewWidget from "../../overview/keyFigureOverviewWidget.js";
import periodReportAttachmentsWidget from "./periodReportAttachmentsWidget.js";
import periodReportCommentWidget from "./periodReportCommentWidget.js";
import PeriodReportSectionWidget from "./PeriodReportSectionWidget.js";
import popoverWidget from "../../../core/widgets/popoverWidget/popoverWidget.js";

/**
 * Visitor that returns a viewer renderable for each visited section.
 *
 * @param {boolean} [spec.canViewAccountingDocuments] - When true, display a link to
 * reports below the condensed report sections.
 * @param {string} lastImportDate - The date of the last import.
 */
export default class PeriodReportSectionWidgetVisitor extends AbstractPeriodReportSectionVisitor {
	constructor({ canViewAccountingDocuments, lastImportDate }) {
		super(...arguments);
		this._canViewAccountingDocuments = canViewAccountingDocuments;
		this._lastImportDate = lastImportDate;
	}

	visitAbstractPeriodReportSection() {
		return () => {};
	}

	visitBusinessOverviewSection(section) {
		const businessOverview = new BusinessOverviewReportWidget();
		const report = section.getPeriodReport();
		businessOverview.show(report.getOverviewData(), report.getPeriod());
		return this._documentSection({
			section,
			content: businessOverview,
			dataTestName: "business-overview-section",
		});
	}

	visitKeyfigureSection(section) {
		const periodReport = section.getPeriodReport();

		const keyFigureOverview = keyFigureOverviewWidget({
			canViewKeyFigure: !periodReport.getPeriod().isConsecutiveMonths(),
			navigateToViewKeyFigureDisableMessage: _(
				"`View KPI` is not available for the selected period",
			),
		});

		keyFigureOverview.show(periodReport.getKeyFigureValues());

		return this._documentSection({
			section,
			content: keyFigureOverview,
			dataTestName: "kpis-section",
			dataHelpName: "kpis-section",
		});
	}

	visitAgedAccountsReceivablesSection(section) {
		const summaryWidget = new AgedAccountsSummaryWidget({
			summary: section.getSummary(),
			baseColor: "#00a4c1",
			importDate: this._lastImportDate,
			linkLabel: _("Open the AR aged report"),
			pathName: "aged-receivables-per-customer",
			textTotal: _("Total receivables"),
			cssClass: "aged-accounts-receivables-summary",
		});

		return this._documentSection({
			section,
			content: summaryWidget,
			dataTestName: "aged-accounts-receivables-section",
		});
	}

	visitAgedAccountsPayablesSection(section) {
		const summaryWidget = new AgedAccountsSummaryWidget({
			summary: section.getSummary(),
			baseColor: "#00a4c1",
			importDate: this._lastImportDate,
			linkLabel: _("Open the AP aged report"),
			pathName: "aged-payables-per-supplier",
			textTotal: _("Total payables"),
			cssClass: "aged-accounts-payables-summary",
		});

		return this._documentSection({
			section,
			content: summaryWidget,
			dataTestName: "aged-accounts-payables-section",
		});
	}

	visitIncomeStatementSection(section) {
		const periodReport = section.getPeriodReport();
		const incomeStatement = condensedStatementWidget({
			cssClass: "condensed-income-statement",
		});

		incomeStatement.setReport(periodReport.getIncomeStatement());
		return this._documentSection({
			section,
			content: [
				incomeStatement,
				this._quickLinkIncomeStatementRenderer(periodReport),
			],
			dataTestName: "income-statement-section",
		});
	}

	visitBalanceSheetSection(section) {
		const periodReport = section.getPeriodReport();
		const balanceSheet = condensedStatementWidget({
			cssClass: "condensed-balance-sheet",
		});
		balanceSheet.setReport(periodReport.getBalanceSheet());

		return this._documentSection({
			section,
			content: [
				balanceSheet,
				this._quickLinkBalanceSheetRenderer(periodReport),
			],
			dataTestName: "balance-sheet-section",
		});
	}

	visitAttachmentSection(section) {
		const attachments = periodReportAttachmentsWidget({
			report: section.getPeriodReport(),
		});

		return this._documentSection({
			section,
			content: attachments,
			dataTestName: "attachments-section",
		});
	}

	_documentSection({ section, content, dataTestName, dataHelpName }) {
		return new PeriodReportSectionWidget({
			section,
			content,
			comment: this._commentRenderer(section),
			dataTestName,
			dataHelpName,
		});
	}

	_commentRenderer(section) {
		if (!section.hasComment()) {
			return () => {};
		}

		return periodReportCommentWidget({
			comment: section.getComment(),
		});
	}

	_quickLinkIncomeStatementRenderer(periodReport) {
		if (!this._canViewAccountingDocuments) {
			return "";
		}

		let period = periodReport.getPeriod();

		if (period.isConsecutiveMonths()) {
			period = period.endMonth();
		}

		return this._quickLinkRenderer({
			periodReport,
			name: _("Income Statement"),
			callback: () =>
				router.getRouter().redirectTo("income-statement", {
					period: period.toString(),
				}),
			navigateToReportWarningMessage: _(
				"`Navigate to report` will show last month of the selected period",
			),
		});
	}

	_quickLinkBalanceSheetRenderer(periodReport) {
		if (!this._canViewAccountingDocuments) {
			return "";
		}

		let period = periodReport.getPeriod();

		if (period.isConsecutiveMonths()) {
			period = period.endMonth();
		}

		return this._quickLinkRenderer({
			periodReport,
			name: _("Balance Sheet"),
			callback: () =>
				router.getRouter().redirectTo("balance-sheet", {
					period: period.toString(),
				}),
			navigateToReportWarningMessage: _(
				"`Navigate to report` will show last month of the selected period",
			),
		});
	}

	_quickLinkRenderer({
		periodReport,
		name,
		callback,
		navigateToReportWarningMessage,
	}) {
		return (html) => {
			let popoverTrigger;

			html.div(
				{ klass: "overview-quicklinks report" },
				html.ul(
					html.li({ class: "classic" }, (html) => {
						popoverTrigger = html.a(
							iconAndText(
								{ space: none, icon: classicReportIcon },
								name,
							),
						);
					}),
				),
			);

			this._makeQuickLinkPopover({
				periodReport,
				triggerItem: popoverTrigger,
				callback,
				navigateToReportWarningMessage,
			});
		};
	}

	_makeQuickLinkPopover({
		periodReport,
		triggerItem,
		callback,
		navigateToReportWarningMessage,
	}) {
		return popoverWidget({
			header: (html) => {
				if (periodReport.isPublished()) {
					html.render(
						_("Report published on {timestamp}", {
							timestamp: formatAsDashedYearMonthDay(
								periodReport.getPublishDate(),
							),
						}),
					);
				} else {
					html.render(_("Unpublished period report"));
				}
			},
			content: (html) => {
				html.span(
					_(
						"Be aware that when navigating from published reports, numbers might be based on data uploaded after the published date.",
					),
				);

				if (navigateToReportWarningMessage) {
					html.div({ class: "popover-divider" });

					html.render(
						new AlertMessageWidget({
							type: "warning",
							content: navigateToReportWarningMessage,
						}),
					);
				}
			},
			primaryLabel: _("Navigate to report"),
			primaryAction: callback,
			triggerItem,
		});
	}
}
